// Billing (doc 03 §4). Rates are a human decision; the system meters what and
// for how long, and flags anything active that nobody has priced.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, type EndpointSummary, type Invoice, type Me, type Page, type Rate } from "../api";
import {
  Badge, Card, Empty, ErrorNote, Field, Modal, PageHeader, Spinner, Stat,
  formatDate, formatMoney, useAction,
} from "../ui";

interface Unpriced {
  count: number;
  endpoints: { endpoint_id: string; service_tag: string; state: string; site: string | null; reseller: string | null; days_unpriced: number | null }[];
}

export default function Billing({ me }: { me: Me }) {
  const [tab, setTab] = useState<"invoices" | "rates">("invoices");
  const isAdmin = me.role === "nci_admin";

  return (
    <>
      <PageHeader title="Billing" subtitle="Provisioned-state metering, manual pricing, monthly prorated invoices." />
      {isAdmin && <UnpricedBanner />}

      <nav className="mb-4 flex gap-1 border-b border-ink-600">
        {(["invoices", "rates"] as const).filter((t) => t === "invoices" || isAdmin).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={tab === key
              ? "border-b-2 border-brand px-4 py-2 text-sm font-medium capitalize text-ink-100"
              : "border-b-2 border-transparent px-4 py-2 text-sm capitalize text-ink-400 hover:text-ink-200"}
          >
            {key}
          </button>
        ))}
      </nav>

      {tab === "invoices" ? <Invoices me={me} /> : <Rates />}
    </>
  );
}

function UnpricedBanner() {
  const { data } = useQuery<Unpriced>({ queryKey: ["unpriced"], queryFn: () => api.get("/v1/reports/unpriced") });
  if (!data?.count) return null;
  return (
    <div className="mb-6 rounded-md border border-state-warn/40 bg-state-warn/10 p-4 text-sm text-state-warn">
      <div className="font-medium">
        {data.count} active Outpost{data.count === 1 ? " has" : "s have"} no rate assigned
      </div>
      <p className="mt-1 text-xs opacity-90">
        These will appear on invoices at $0, flagged — never silently omitted. Assign a rate on the Rates tab.
      </p>
      <ul className="mt-2 space-y-1 text-xs">
        {data.endpoints.slice(0, 8).map((endpoint) => (
          <li key={endpoint.endpoint_id}>
            <Link to={`/endpoints/${endpoint.endpoint_id}`} className="font-mono underline">{endpoint.service_tag}</Link>
            {endpoint.site ? ` · ${endpoint.site}` : ""}
            {endpoint.days_unpriced != null ? ` · unpriced for ${endpoint.days_unpriced} days` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Invoices({ me }: { me: Me }) {
  const [state, setState] = useState("");
  const [running, setRunning] = useState(false);
  const queryClient = useQueryClient();
  const { run, busy } = useAction();

  const { data, isLoading, error } = useQuery<Page<Invoice>>({
    queryKey: ["invoices", state],
    queryFn: () => api.get(`/v1/invoices?${new URLSearchParams(state ? { state } : {})}`),
  });

  const totals = (data?.items ?? []).reduce(
    (acc, invoice) => {
      acc.total += parseFloat(invoice.total);
      acc.outstanding += parseFloat(invoice.balance ?? invoice.total);
      return acc;
    },
    { total: 0, outstanding: 0 },
  );

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Invoices" value={data?.total ?? 0} />
        <Stat label="Invoiced" value={formatMoney(totals.total)} />
        <Stat label="Outstanding" value={formatMoney(totals.outstanding)} tone={totals.outstanding > 0 ? "warn" : "ok"} />
        <Stat label="Drafts" value={(data?.items ?? []).filter((i) => i.state === "draft").length} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select className="input max-w-[10rem]" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">All states</option>
          <option value="draft">draft</option>
          <option value="final">final</option>
          <option value="void">void</option>
        </select>
        {me.role === "nci_admin" && (
          <button className="btn-secondary" onClick={() => setRunning(true)}>Run billing for a period…</button>
        )}
      </div>

      <Card padded={false}>
        {isLoading ? <Spinner /> : error ? <div className="p-4"><ErrorNote error={error} /></div> : !data?.items.length ? (
          <Empty>No invoices yet. The monthly run creates drafts on the 1st.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>{me.role !== "reseller" && <th>Reseller</th>}<th>Period</th>
                  <th>State</th><th>Total</th><th>Paid</th><th>Balance</th><th>Due</th><th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="font-mono text-xs">{invoice.number ?? `draft #${invoice.id}`}</td>
                    {me.role !== "reseller" && <td>{invoice.reseller_name}</td>}
                    <td className="text-ink-300">{formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}</td>
                    <td>
                      <Badge tone={invoice.state === "final" ? "ok" : invoice.state === "void" ? "idle" : "info"}>
                        {invoice.state}
                      </Badge>
                    </td>
                    <td className="tabular-nums">{formatMoney(invoice.total, invoice.currency)}</td>
                    <td className="tabular-nums text-ink-400">{formatMoney(invoice.paid ?? 0, invoice.currency)}</td>
                    <td className={parseFloat(invoice.balance ?? "0") > 0 ? "tabular-nums text-state-warn" : "tabular-nums"}>
                      {formatMoney(invoice.balance ?? 0, invoice.currency)}
                    </td>
                    <td className="text-ink-400">{formatDate(invoice.due_date)}</td>
                    <td className="text-right">
                      <Link to={`/invoices/${invoice.id}`} className="text-brand-on hover:underline">open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {running && (
        <RunModal
          onClose={() => setRunning(false)}
          onDone={() => { setRunning(false); queryClient.invalidateQueries({ queryKey: ["invoices"] }); }}
          run={run}
          busy={busy}
        />
      )}
    </>
  );
}

function RunModal({ onClose, onDone, run, busy }: {
  onClose: () => void; onDone: () => void;
  run: (fn: () => Promise<unknown>, msg?: string) => Promise<boolean>; busy: boolean;
}) {
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const [period, setPeriod] = useState(lastMonth.toISOString().slice(0, 7));

  return (
    <Modal
      title="Run monthly billing"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() => run(async () => { await api.post(`/v1/billing/run?period=${period}`); onDone(); }, "Billing run complete")}
          >
            {busy ? "Running…" : "Run"}
          </button>
        </>
      }
    >
      <Field label="Period" hint="Creates or refreshes the draft invoices for that month. Finalised invoices are never touched.">
        <input className="input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
      </Field>
      <p className="mt-3 text-xs text-ink-400">
        Lines are prorated daily for partial months. Period boundaries are America/Chicago.
      </p>
    </Modal>
  );
}

function Rates() {
  const [editing, setEditing] = useState<EndpointSummary | null>(null);
  const queryClient = useQueryClient();

  const { data: endpoints } = useQuery<Page<EndpointSummary>>({
    queryKey: ["endpoints"],
    queryFn: () => api.get("/v1/endpoints?limit=1000"),
  });
  const { data: rates } = useQuery<Page<Rate>>({
    queryKey: ["rates"],
    queryFn: () => api.get("/v1/billing/rates"),
  });

  const currentRate = (endpointId: string) =>
    (rates?.items ?? []).filter((r) => r.endpoint_id === endpointId).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];

  return (
    <>
      <Card padded={false}>
        {!endpoints?.items.length ? (
          <Empty>No Outposts to price.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Service tag</th><th>Site</th><th>Reseller</th><th>State</th>
                  <th>Monthly rate</th><th>Per-channel addon</th><th>Effective from</th><th />
                </tr>
              </thead>
              <tbody>
                {endpoints.items.map((endpoint) => {
                  const rate = currentRate(endpoint.id);
                  const billable = endpoint.state === "active" || endpoint.state === "provisioned";
                  return (
                    <tr key={endpoint.id} className={billable && !rate ? "bg-state-warn/5" : undefined}>
                      <td><Link to={`/endpoints/${endpoint.id}`} className="service-tag hover:underline">{endpoint.service_tag}</Link></td>
                      <td className="text-ink-300">{endpoint.site_name ?? "—"}</td>
                      <td className="text-ink-300">{endpoint.reseller_name ?? "—"}</td>
                      <td className="text-ink-400">{endpoint.state}</td>
                      <td className="tabular-nums">
                        {rate ? formatMoney(rate.monthly_rate, rate.currency) : <span className="text-state-warn">unpriced</span>}
                      </td>
                      <td className="tabular-nums text-ink-400">
                        {rate?.per_channel_addon_rate ? formatMoney(rate.per_channel_addon_rate, rate.currency) : "—"}
                      </td>
                      <td className="text-ink-400">{rate ? formatDate(rate.effective_from) : "—"}</td>
                      <td className="text-right"><button className="btn-ghost" onClick={() => setEditing(endpoint)}>set rate</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <RateModal
          endpoint={editing}
          history={(rates?.items ?? []).filter((r) => r.endpoint_id === editing.id)}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); queryClient.invalidateQueries({ queryKey: ["rates"] }); queryClient.invalidateQueries({ queryKey: ["unpriced"] }); }}
        />
      )}
    </>
  );
}

function RateModal({ endpoint, history, onClose, onDone }: {
  endpoint: EndpointSummary; history: Rate[]; onClose: () => void; onDone: () => void;
}) {
  const [monthly, setMonthly] = useState("");
  const [addon, setAddon] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const { run, busy } = useAction();

  return (
    <Modal
      title={`Rate for ${endpoint.service_tag}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy || !monthly}
            onClick={() => run(async () => {
              await api.post("/v1/billing/rates", {
                endpoint_id: endpoint.id,
                monthly_rate: monthly,
                per_channel_addon_rate: addon || null,
                effective_from: effective,
                note: note || null,
              });
              onDone();
            }, "Rate set")}
          >
            {busy ? "Saving…" : "Set rate"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-ink-400">
          QAM sites are per-TV and not electronically auditable, so the price is a human decision. Rates are
          never edited in place — each change is a new row with its own effective date, so any invoice can be
          re-derived from what was true during its period.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Monthly rate">
            <input className="input" type="number" step="0.01" placeholder="450.00" value={monthly} onChange={(e) => setMonthly(e.target.value)} />
          </Field>
          <Field label="Per-channel addon (optional)">
            <input className="input" type="number" step="0.01" value={addon} onChange={(e) => setAddon(e.target.value)} />
          </Field>
        </div>
        <Field label="Effective from"><input className="input" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} /></Field>
        <Field label="Note"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>

        {history.length > 0 && (
          <div>
            <div className="label">Rate history</div>
            <ul className="space-y-1 text-xs">
              {history.sort((a, b) => b.effective_from.localeCompare(a.effective_from)).map((rate) => (
                <li key={rate.id} className="flex justify-between border-b border-ink-700 py-1">
                  <span>{formatDate(rate.effective_from)}</span>
                  <span className="tabular-nums">{formatMoney(rate.monthly_rate, rate.currency)}</span>
                  <span className="text-ink-400">{rate.set_by ?? ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
